import { toast } from "sonner";
import {
  Sprout, GraduationCap, Landmark, Building2, FlaskConical, Puzzle, Loader2, Power,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAdminPlugins, useTogglePlugin } from "@/hooks/useSettings";
import { apiError } from "@/lib/api";

const ICONS = {
  sprout: Sprout,
  "graduation-cap": GraduationCap,
  landmark: Landmark,
  building: Building2,
  flask: FlaskConical,
};

function PluginRow({ plugin, onToggle, pending }) {
  const Icon = ICONS[plugin.icon] || Puzzle;
  return (
    <div
      data-testid={`plugin-row-${plugin.id}`}
      className={`flex items-center gap-4 rounded-xl border p-4 transition-colors ${
        plugin.enabled ? "border-blue-200 bg-white" : "border-slate-200 bg-slate-50"
      }`}
    >
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${
          plugin.enabled ? "bg-primary text-white" : "bg-slate-200 text-slate-500"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-sm text-slate-900 truncate">{plugin.name}</span>
          <span
            className={`text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded ${
              plugin.kind === "vibe" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"
            }`}
          >
            {plugin.kind}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5 truncate">{plugin.description}</p>
        {plugin.nav_path && (
          <p className="text-[10px] font-mono text-slate-400 mt-0.5">nav · {plugin.nav_path}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[11px] font-bold ${plugin.enabled ? "text-emerald-600" : "text-slate-400"}`}>
          {plugin.enabled ? "ON" : "OFF"}
        </span>
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        ) : (
          <Switch
            checked={plugin.enabled}
            onCheckedChange={(v) => onToggle(plugin.id, v)}
            data-testid={`plugin-toggle-${plugin.id}`}
            aria-label={`Toggle ${plugin.name}`}
          />
        )}
      </div>
    </div>
  );
}

export function PluginManager() {
  const { data: plugins = [], isLoading } = useAdminPlugins();
  const toggle = useTogglePlugin({
    onSuccess: () => toast.success("Plugin updated"),
    onError: (e) => toast.error(apiError(e)),
  });

  const onToggle = (id, enabled) => toggle.mutate({ id, enabled });
  const pendingId = toggle.isPending ? toggle.variables?.id : null;

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500" data-testid="plugin-manager-loading">Loading plugins…</div>;
  }

  const vibes = plugins.filter((p) => p.kind === "vibe");
  const apps = plugins.filter((p) => p.kind !== "vibe");

  return (
    <div className="space-y-6" data-testid="plugin-manager">
      <div className="flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
        <Power className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-indigo-900">Dojo Core — Plugin Switchboard</p>
          <p className="text-xs text-indigo-700/80 mt-0.5">
            Toggle vibes and app modules globally. Turning one OFF hides it from navigation for
            everyone; its routes stay live. Changes apply instantly across the app.
          </p>
        </div>
      </div>

      {vibes.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Vibe Modules</h3>
          {vibes.map((p) => (
            <PluginRow key={p.id} plugin={p} onToggle={onToggle} pending={pendingId === p.id} />
          ))}
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-700">App Modules</h3>
        {apps.map((p) => (
          <PluginRow key={p.id} plugin={p} onToggle={onToggle} pending={pendingId === p.id} />
        ))}
      </section>
    </div>
  );
}
